import {
    buildOptionalParams,
    buildPath,
    buildTemplatePath,
    Card,
    CrudMode,
    Dialog,
    handleClose,
    Page,
    PageRoute,
    ScreenType,
    useParams,
    useUnsavedChanges,
    useValidationErrors,
} from "@compass/core";
import { Region, RegionEditResource, RegionViewResource, Permission } from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { RegionPageBody } from "../RegionPage/RegionPageBody";
import { RegionPageHeader } from "./RegionPageHeader";

const routeName = "region";
class Params {
    id: number = 0;
    crudMode: CrudMode = "view";
    screenType: ScreenType = "standard";
}

export const regionPageRoute: PageRoute<Params> = {
    handle: "Region",
    templatePath: buildTemplatePath(routeName, Params),
    getPath: (params) => buildPath(routeName, params),
};

export const RegionPage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;
    const navigate = useNavigate();
    const { params } = useParams(routeName, Params);
    const [region, setRegion] = useState<Region>({} as Region);
    const [initRegion, setInitRegion] = useState<Region>(region);
    const [viewResource, setViewResource] = useState<RegionViewResource>({});
    const [editResource, setEditResource] = useState<RegionEditResource>({});
    const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [region] });
    const { validationErrors, setValidationErrors } = useValidationErrors();

    useEffect(() => {
        fetch(params.id);
        ApiService.regions.getViewResource().then((response) => {
            setViewResource(response);
        });
    }, []);

    useEffect(() => {
        if (params.crudMode === "edit") {
            ApiService.regions.getEditResource().then((response) => {
                setEditResource(response);
            });
        }
    }, [params.crudMode]);

    const reroute = (id: number, crudMode: CrudMode) =>
        navigate(`../${regionPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType, })}`);

    const fetch = (id: number) => {
        ApiService.regions.get(id).then((response) => {
            setRegion(response);
            setInitRegion(response);
            resetUnsavedChanges([response]);
        });
    };

    return (
        <Dialog
            isForm
            open={true}
            onClose={() => handleClose(navigate, unsavedChanges)}
            maxWidth="md"
            header={
                <RegionPageHeader
                    rootPermission={ROOT_PERMISSION}
                    handle={regionPageRoute.handle}
                    crudMode={params.crudMode}
                    reroute={reroute}
                    fetch={fetch}
                    region={region}
                    setRegion={setRegion}
                    initRegion={initRegion}
                    setValidationErrors={setValidationErrors}
                    unsavedChanges={unsavedChanges}
                    resetUnsavedChanges={resetUnsavedChanges}
                />
            }
            content={
                <RegionPageBody
                    region={region}
                    setRegion={setRegion}
                    viewResource={viewResource}
                    editResource={editResource}
                    crudMode={params.crudMode}
                    validationErrors={validationErrors}
                />
            }
        />
    );
};